import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Dialog, Button, DialogActions, DialogContent, DialogTitle, LinearProgress, FormControl, FormControlLabel, Typography, Radio, RadioGroup } from "@material-ui/core";
import { ClubSelect, PlainTextField } from "components";
import { respondMembershipRequestsBulk, getMembershipRequests } from "api/admin/members";
import i18n from "./i18n";

// prop types
interface RequestsBulkEditDialogProps {
    showDialog: boolean,
    onClose: () => void,
    onRefreshRequestsTable: () => void,
}

// admin membership requests bulk edit dialog
const RequestsBulkEditDialog = ({ showDialog, onClose, onRefreshRequestsTable }: RequestsBulkEditDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [approve, setApprove] = useState<boolean>(true);
    const [affectedEntries, setAffectedEntries] = useState(0);
    const [clubId, setClubId] = useState<string | undefined>();
    const [query, setQuery] = useState("");
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);

    // update affected entries count
    useEffect(() => {
        getMembershipRequests({ clubId, query }).then(({ totalCount }) => setAffectedEntries(totalCount));
    }, [clubId, query]);

    // execute bulk action
    const executeAction = async () => {
        setShowLoadingAnimation(true);
        try {
            await respondMembershipRequestsBulk({ approve, clubId, query });
            onRefreshRequestsTable(); // fetch update data
            onClose();
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // render
    return (
        <Dialog
            fullWidth
            open={showDialog}
            onClose={onClose}
        >
            <DialogTitle>{i18n.bulkEdit}</DialogTitle>
            <DialogContent dividers>

                {/* club */}
                <ClubSelect
                    clubId={clubId}
                    controlProps={{ fullWidth: true, margin: "normal" }}
                    userRoles={["admin", "owner"]}
                    onChange={setClubId}
                />

                {/* query */}
                <PlainTextField
                    label={i18n.query}
                    value={query}
                    variant="standard"
                    onChange={(e) => setQuery(e.target.value)}
                />

                {/* affected entries */}
                <Typography align="right" variant="subtitle2">
                    {i18n.affectedEntries(affectedEntries)}
                </Typography>

                {/* approve */}
                <FormControl margin="normal">
                    <RadioGroup
                        row
                        value={approve ? "true" : "false"}
                        onChange={(e) => setApprove(e.target.value === "true")}
                    >
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            label={i18n.accept}
                            value="true"
                        />
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            label={i18n.decline}
                            value="false"
                        />
                    </RadioGroup>
                </FormControl>

            </DialogContent>
            {showLoadingAnimation && (
                <LinearProgress />
            )}
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    {i18n.cancel}
                </Button>
                <Button onClick={executeAction} color="primary">
                    {i18n.execute}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RequestsBulkEditDialog;
